import React, { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import OnboardingWizard from './OnboardingWizard';
import { useAuth } from '../contexts/AuthContext';
import { apiGet } from '../lib/api';

const ONBOARDING_DISMISS_KEY = 'ellora_onboarding_dismissed';

export default function Layout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auto-close the mobile drawer whenever route changes
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // respect the org-wide onboarding toggles set by super_admin.
  //
  // SUPER_ADMIN: shown if workspace is empty AND user hasn't dismissed it
  //              AND wizard_enabled_admin is true.
  // REGULAR USER: shown on EVERY login until ALL 4 mandatory steps are complete
  //               (profile fields, password, tour, HR policies) — unless
  //               wizard_enabled_users is set to false by super_admin.
  useEffect(() => {
    if (!user) return;
    const isSuper = user.role === 'super_admin';
    apiGet('/settings/onboarding').catch(() => ({})).then(cfg => {
      const adminOn = cfg.wizard_enabled_admin !== false;
      const usersOn = cfg.wizard_enabled_users !== false;

      if (isSuper) {
        if (!adminOn) return; // super_admin turned it off for themselves / org
        if (cfg.admin_wizard_dismissed === true) return; // dismissed server-side (persists across devices)
        if (localStorage.getItem(ONBOARDING_DISMISS_KEY) === '1') return;
        apiGet('/hr/employees').catch(() => []).then((emps) => {
          const eList = Array.isArray(emps) ? emps : (emps?.employees || []);
          if (eList.length === 0) setShowOnboarding(true);
        });
      } else {
        if (!usersOn) return; // wizard disabled org-wide for users
        Promise.all([
          apiGet('/hr/me').catch(() => null),
          apiGet('/hr/policies/me/pending').catch(() => ({ pending: [] })),
        ]).then(([me, pendRes]) => {
          const emp = me?.employee;
          const profileOk = !!(emp && emp.phone && emp.emergency_contact);
          // Password change is enforced at LOGIN (Login.jsx), so it is no longer
          // an onboarding-wizard gate.
          const policiesOk = (pendRes?.pending || []).length === 0;
          const tourOk = JSON.parse(localStorage.getItem('ellora_onboarding_status') || '{}').tour === true;
          if (!(profileOk && policiesOk && tourOk)) {
            setShowOnboarding(true);
          }
        });
      }
    });
  }, [user]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 font-mono text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="h-screen flex bg-[#FDFDFD]">
      <Sidebar mobileOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          onOpenMenu={() => setMobileMenuOpen(true)}
        />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          <Outlet />
        </main>
      </div>
      {showOnboarding && (
        <OnboardingWizard user={user} onClose={() => setShowOnboarding(false)} />
      )}
    </div>
  );
}
